import Axios, { AxiosError } from "axios";

import { IDownloadProgressData, IOnUploadProgress } from "@Components/activity/hooks/useActivityUpload";
import { IEmptyObject } from "@Components/shared/types";
import { EightMB } from "@Constants";
import Result from "@Core/Result";
import { ServiceBase } from "@Core/ServiceBase";
import {
    IAbortedUpload,
    IActivityCreationDetails,
    IActivityCreationResult,
    IActivityDefinitions,
    IActivityDetails,
    IActivityExportMetadataRequest,
    IActivityMetaDetails,
    IActivityPayload,
    ICommentDetails,
    IMediaServiceUploadResponseSchema,
    IPartETag,
    IUploadResultDetails,
    IUploadedDetails,
    IUserActivityMinimal,
} from "@Models/IActivityDetails";
import { IEntity } from "@Models/IEntity";
import { IPdfItem } from "@Models/IPdfItem";
import { IPointsMetadata, IPointsMetadataRequest } from "@Models/IPoints";
import { camelToTitle } from "@Utils";

const API_URL = "/api/Activity";

export default class ActivityService extends ServiceBase {
    /**
     * Fetch activities by instance id.
     * @param activityInstanceIds
     */
    public async getActivities(
        activityInstanceIds: string[] = [],
        isPublic = false
    ): Promise<Result<IActivityDetails[]>> {
        return await this.requestJson<IActivityDetails[]>({
            url: `/api/Activity/GetActivities/${activityInstanceIds.join(",")}${isPublic ? "?is-public=true" : ""}`,
            method: "GET",
        });
    }

    /**
     * Create a new activity instance. (Uses API version 2)
     * @param userId
     * @param model
     */
    public async create(userId: string, model: IActivityCreationDetails): Promise<Result<IActivityCreationResult>> {
        return await this.requestJson<IActivityCreationResult>({
            url: `/api/Activity/Create/${userId}?v=2`,
            method: "POST",
            data: model,
        });
    }

    /**
     * Manage a data or media upload for the activity instance provided.
     * @param activityInstanceId
     * @param model
     */
    public async manageUpload(
        activityInstanceId: string,
        model: IUploadResultDetails
    ): Promise<Result<IUploadedDetails>> {
        return await this.requestJson({
            url: `/api/Activity/ManageUpload/${activityInstanceId}`,
            method: "PATCH",
            data: model,
        });
    }

    /**
     * Upload a file part to a pre-signed S3 url.
     * @param file
     * @param url
     * @param partNumber
     * @param totalParts
     * @param chunkSize
     */
    public async uploadS3Part(
        file: File,
        url: string,
        partNumber = 1,
        totalParts = 1,
        chunkSize = EightMB,
        onUploadProgress?: IOnUploadProgress
    ): Promise<IPartETag | IAbortedUpload> {
        const axios = Axios.create();

        // we are using the cancel token to abort the request
        const CancelToken = Axios.CancelToken;
        const source = CancelToken.source();

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        delete axios.defaults.headers.put["Content-Type"];
        const start = (partNumber - 1) * chunkSize;
        const end = partNumber * chunkSize;
        const blob = file.slice(start, end, file.type);
        const res = await axios
            .put(url, blob, {
                cancelToken: source.token,
                onUploadProgress: (progress: IDownloadProgressData) => {
                    return onUploadProgress?.({ partNumber, progress, totalParts });
                },
            })
            .catch((err: AxiosError) => {
                if (err.isAxiosError && err.response) {
                    return err.response;
                } else {
                    return new Error(err.message);
                }
            });

        // we are checking that the etags are present in the response
        const response = IMediaServiceUploadResponseSchema.safeParse(res);

        if (!response.success) {
            return { abort: source.cancel, error: res };
        }

        const eTag = response.data.headers.etag.replace(/"/g, "");
        return { eTag, partNumber };
    }

    /**
     * Fetch activities by user. Note that activity refers to type id, not instance id.
     * @param user
     * @param activity
     * @param category
     * @param dateFrom
     * @param dateTo
     * @param page
     */
    public async getUser(
        user: string[] = [],
        activity: string[] = [],
        category: string[] = [],
        dateFrom = "",
        dateTo = "",
        page = 0,
        isPublic = false,
        includeCMEPublic = false,
        loggedInUserCME: string = null
    ): Promise<Result<IActivityPayload>> {
        const str: string[] = [];
        if (loggedInUserCME !== null) {
            str.push("loggedin-userid-cme=" + loggedInUserCME);
        }
        if (activity.length > 0) {
            str.push("activity=" + activity.join(","));
        }
        if (category.length > 0) {
            str.push("category=" + category.join(","));
        }
        if (dateFrom !== "" && dateTo !== "") {
            str.push("from-date=" + dateFrom);
            str.push("to-date=" + dateTo);
        }
        if (isPublic) {
            str.push("is-public=true");
        }
        if (includeCMEPublic) {
            str.push("include-cme-public=true");
        }
        str.push("page=" + page.toString().replace("null", "0"));
        return await this.requestJson<IActivityPayload>({
            url: `/api/Activity/GetUser/${user.join(",")}?` + str.join("&"),
            method: "GET",
        });
    }

    /**
     * Delete an activity instance.
     * @param activityInstanceId
     */
    public async delete(activityInstanceId: string): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `/api/Activity/Delete/${activityInstanceId}`,
            method: "DELETE",
        });
    }

    /**
     * Create a new activity instance with the payload provided.
     * @param model
     *
     * @deprecated Use `create` instead.
     * ? Review and remove if not needed.
     */
    public async add(model: IActivityDetails): Promise<Result<number>> {
        return await this.requestJson<number>({
            url: "/api/Activity/Add",
            method: "POST",
            data: model,
        });
    }

    /**
     * Remove an activity comment.
     * @param commentId
     */
    public async removeComment(commentId: string): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `/api/Comment/Remove/${commentId}`,
            method: "DELETE",
        });
    }

    /**
     * Create a new activity comment with the payload provided.
     * @param model
     */
    public async addComment(model: ICommentDetails): Promise<Result<IEntity>> {
        return await this.requestJson<IEntity>({
            url: `/api/Comment/Create/${model.activityInstanceId}`,
            method: "POST",
            data: model,
        });
    }

    /**
     * Add or update an activity meta item associated with an activity instance
     * @param model
     */
    public async update(model: IActivityMetaDetails): Promise<Result<IEntity>> {
        return await this.requestJson<IEntity>({
            url: `/api/Activity/Update/${model.activityInstanceId}`,
            method: "POST",
            data: model,
        });
    }

    /**
     * Update an existing activity comment with the payload provided.
     * @param model
     */
    public async updateComment(model: ICommentDetails): Promise<Result<boolean>> {
        return await this.requestJson<boolean>({
            url: `/api/Comment/Update/${model.activityCommentId}`,
            method: "PATCH",
            data: model,
        });
    }

    /**
     * Fetch activity definitions.
     */
    public async getDefinitions(): Promise<Result<IActivityDefinitions>> {
        return await this.requestJson<IActivityDefinitions>({
            url: `/api/Activity/GetTypes`,
            method: "GET",
        });
    }

    /**
     * Get minimal list of activity instances for the user provided.
     * @param userId
     */
    public async getUserMinimal(userId: string): Promise<Result<IUserActivityMinimal[]>> {
        return await this.requestJson<IUserActivityMinimal[]>({
            url: `/api/Activity/GetUserMinimal/${userId}`,
            method: "GET",
        });
    }

    /**
     * Get the points metadata for the user provided.
     * @param userId
     */
    public async getPointsMetadata(userId: string): Promise<Result<IPointsMetadata[]>> {
        return await this.requestJson<IPointsMetadata[]>({
            url: `${API_URL}/GetPointsMetadata/${userId}`,
            method: "GET",
        });
    }

    /**
     * Get a specific points report for the user provided.
     * @param pointsMetadataId
     * @param userId
     */
    public async getPointsReport(pointsMetadataId: string, userId: string): Promise<Result<IPdfItem[]>> {
        return await this.requestJson<IPdfItem[]>({
            url: `${API_URL}/GetPointsReport/${userId}?points-id=${pointsMetadataId}`,
            method: "GET",
        });
    }

    /**
     * Create a metadata request export or points given user from the given activity instanceIds
     */
    public async createMetadata(
        req: IActivityExportMetadataRequest | IPointsMetadataRequest,
        type: "export" | "points"
    ): Promise<Result<IEntity>> {
        // start date and end date are optional
        const terms = !!req.startDate && !!req.endDate ? `from-date=${req.startDate}&to-date=${req.endDate}` : "";

        // the ids
        const ids = req.activityInstanceIds.join(",");

        // the base url
        const baseUrl = `${API_URL}/Create${camelToTitle(type)}Metadata/${req.ownerId}/${ids}/?${terms}`;

        // if pointsType is provided, add it to the url
        const url = "pointsType" in req ? `${baseUrl}&points-type=${req.pointsType}` : baseUrl;

        return await this.requestJson<IEntity>({
            url,
            method: "POST",
        });
    }
    /**
     * Create a export metadata request for the given user from the given activity instanceIds
     */
    public async createExportMetadata(req: IActivityExportMetadataRequest): Promise<Result<IEntity>> {
        return await this.createMetadata(req, "export");
    }

    /**
     * Create a points metadata request for the given user from the given activity instanceIds
     */
    public async createPointsMetadata(req: IPointsMetadataRequest): Promise<Result<IEntity>> {
        return await this.createMetadata(req, "points");
    }
}
